import { PureComponent } from "react";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import AssetComponent from "components/core/asset/asset-component";
import Button from "segments/desktop/core-components/button/button";
import Colors from "styles/colors";
import { getImagePositionStyles } from "segments/desktop/core-components/images-grid/images-grid-service";
import classnames from "classnames";
import Title from "segments/desktop/core-components/title/title";
import VerticalTimerTabComponent from "./vertical-timer-tab-desktop/vertical-timer-tab-desktop-component";
import { componentStyles } from "./timer-vertical-tabs-desktop-component.scss";
import { DEFAULT_TAB_TIMER } from "../../constants/timer";

export default class TimerVerticalTabsDesktopComponent extends PureComponent {
  constructor(props) {
    super(props);

    const { isCollapsable, selectedTabIndex } = this.props;
    this.state = {
      selectedTab: selectedTabIndex ?? isCollapsable ? null : 0,
      forceProgressbarDone: false
    };

    this.tabsInterval = null;

    this.handleOnMouseLeave = this.handleOnMouseLeave.bind(this);
    this.handleOnMouseOver = this.handleOnMouseOver.bind(this);
    this.setSelectedTab = this.setSelectedTab.bind(this);
    this.startTabInterval = this.startTabInterval.bind(this);
    this.clearTabInterval = this.clearTabInterval.bind(this);
    this.startInterval = this.startInterval.bind(this);
    this.stopInterval = this.stopInterval.bind(this);
    this.renderTabs = this.renderTabs.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { selectedTabIndex } = this.props;
    selectedTabIndex !== prevProps.selectedTabIndex && this.setState({ selectedTab: selectedTabIndex });
  }

  componentDidMount() {
    const { timerEnabled, onTabChangedHandler, tabs } = this.props;
    const { selectedTab } = this.state;

    onTabChangedHandler?.(tabs[selectedTab]);

    if (!timerEnabled) return;

    this.startInterval();
  }

  handleOnMouseOver() {
    const { timerEnabled } = this.props;
    if (!timerEnabled) return;

    this.setState({ forceProgressbarDone: true });
    this.stopInterval();
  }

  handleOnMouseLeave() {
    const { timerEnabled } = this.props;
    if (!timerEnabled) return;

    this.setState({ forceProgressbarDone: false });
    this.startInterval();
  }

  setSelectedTab(newSelectedTab) {
    const { isCollapsable, onTabChangedHandler, tabs } = this.props;
    const { selectedTab } = this.state;

    const tabToSelect = isCollapsable && newSelectedTab === selectedTab ? null : newSelectedTab;

    onTabChangedHandler?.(tabs[tabToSelect]);
    this.setState({ selectedTab: tabToSelect, forceProgressbarDone: false });
    this.startInterval();
  }

  startTabInterval() {
    const { selectedTab } = this.state;
    const { tabs, isCollapsable } = this.props;

    if (isCollapsable) return;

    this.tabsInterval = setInterval(() => {
      const nextTab = (selectedTab + 1) % tabs.length;
      this.setSelectedTab(nextTab);
    }, parseInt(tabs[selectedTab]?.timer) || DEFAULT_TAB_TIMER);
  }

  clearTabInterval() {
    const { isCollapsable } = this.props;
    if (isCollapsable) return;

    clearInterval(this.tabsInterval);
  }

  startInterval() {
    const { timerEnabled } = this.props;

    if (timerEnabled) {
      this.stopInterval();
      this.startTabInterval();
    }
  }

  stopInterval() {
    const { timerEnabled } = this.props;

    if (timerEnabled) {
      this.clearTabInterval();
    }
  }

  renderTabs() {
    const { selectedTab, forceProgressbarDone } = this.state;
    const {
      tabs,
      showDivider,
      timerEnabled,
      tabContentRenderer,
      showArrow,
      progressbarColor,
      alwaysShowProgressbar,
      arrowSize,
      tabTitleRenderer,
      title,
      getTabIdentifier
    } = this.props;
    const { fontSize, selectedFontSize, fontColor, selectedFontColor, fontWeight, lineHeight } = title || {};

    return tabs.map((tab, index) => {
      const { title, content, badgeText, timer, overrideTabFontColor, ...rest } = tab;
      return (
        <div className="tab" role="tablist">
          <VerticalTimerTabComponent
            key={index}
            index={index}
            getTabIdentifier={getTabIdentifier}
            isSelected={index === selectedTab}
            title={title}
            content={content}
            badgeText={badgeText}
            duration={timer || DEFAULT_TAB_TIMER}
            forceProgressbarDone={forceProgressbarDone}
            setSelectedTab={this.setSelectedTab}
            showArrow={showArrow}
            timerEnabled={timerEnabled}
            fontSize={fontSize}
            lineHeight={lineHeight}
            selectedFontSize={selectedFontSize}
            fontColor={overrideTabFontColor ?? fontColor}
            selectedFontColor={overrideTabFontColor ?? selectedFontColor}
            progressbarColor={progressbarColor}
            alwaysShowProgressbar={alwaysShowProgressbar}
            arrowSize={arrowSize}
            tabTitleRenderer={tabTitleRenderer}
            tabContentRenderer={tabContentRenderer}
            titleFontWeight={fontWeight}
            {...rest}
          />
          {showDivider && index < tabs.length - 1 && <div className="tabs-divider" />}
        </div>
      );
    });
  }

  renderAllRightSideContent() {
    const { tabs } = this.props;
    const { selectedTab } = this.state;

    return (
      <div className="right-side-container">
        {tabs.map((tab, index) => (
          <div
            key={index}
            className={classnames("right-side-tab-content-container", { hidden: index !== selectedTab })}
          >
            {this.renderRightSide(index)}
          </div>
        ))}
      </div>
    );
  }

  renderRightSide(selectedTab) {
    const { assetQuality, hideControls, useWindowMask, tabs, backgroundColor, rightSideRenderer, assetPosition } =
      this.props;

    const shouldDisplayImage =
      (tabs[selectedTab]?.asset && tabs[selectedTab].asset?.image?.src) || tabs[selectedTab]?.asset?.video?.src;

    if (!shouldDisplayImage && rightSideRenderer) return rightSideRenderer(tabs[selectedTab]);

    const assetPositionStyle = getImagePositionStyles(assetPosition);
    const assetInnerStyle = { ...assetPositionStyle, backgroundColor: Colors[backgroundColor] };

    return (
      <div className="desktop-asset-container">
        <AssetComponent
          {...tabs[selectedTab]?.asset}
          assetQuality={assetQuality}
          hideControls={hideControls}
          useWindowMask={useWindowMask}
          convertGifToVideo={true}
          className="tab-asset-inner"
          imageClassName="tab-asset-image"
          style={assetInnerStyle}
        />
      </div>
    );
  }

  render() {
    const {
      showButton,
      buttonConfig,
      isFullScreen,
      tabsTitle,
      tabsContainerClassName,
      isTabsFullWidth,
      prerenderTabs
    } = this.props;
    const { selectedTab } = this.state;

    return (
      <div className={classnames("timer-vertical-tabs-desktop-component", { "full-screen": isFullScreen })}>
        <div className="vertical-timer-tabs-container">
          <div
            className={classnames("desktop-tabs-text-container", { [tabsContainerClassName]: tabsContainerClassName })}
            style={{ width: isTabsFullWidth && "100%" }}
            onFocus={this.handleOnMouseOver}
            onBlur={this.handleOnMouseLeave}
            onMouseOver={this.handleOnMouseOver}
            onMouseLeave={this.handleOnMouseLeave}
          >
            {tabsTitle?.title && (
              <div className="tabs-paragraph-wrapper">
                <Title {...tabsTitle} />
              </div>
            )}
            {this.renderTabs()}
          </div>
          <SwitchTransition mode="out-in">
            <CSSTransition key={selectedTab} classNames="fade" timeout={200}>
              {prerenderTabs ? this.renderAllRightSideContent() : this.renderRightSide(selectedTab)}
            </CSSTransition>
          </SwitchTransition>
        </div>
        {showButton && (
          <div className="button-link">
            <Button {...buttonConfig} />
          </div>
        )}
        <style jsx>{componentStyles}</style>
      </div>
    );
  }
}
